export const WIZARD_CHARACTERISTICS = [
  {
    formKey: "rooms",
    wdKey: "rooms",
    label: "Ambientes",
    placeholder: "Ej: 3",
    required: true,
    minValue: 1,
    minFilter: "minRooms",
    maxFilter: "maxRooms",
    expandExact: (value) => ({
      min: Math.max(0, value - 1),
      max: value + 1,
    }),
  },
  {
    formKey: "bedrooms",
    wdKey: "bedrooms",
    label: "Dormitorios",
    placeholder: "Ej: 3",
    required: true,
    minValue: 0,
    minFilter: "minBedrooms",
    maxFilter: "maxBedrooms",
    expandExact: (value) => ({
      min: Math.max(0, value - 1),
      max: value + 1,
    }),
  },
  {
    formKey: "bathrooms",
    wdKey: "bathrooms",
    label: "Baños",
    placeholder: "Ej: 2",
    required: true,
    minValue: 1,
    minFilter: "minBathrooms",
    maxFilter: "maxBathrooms",
    expandExact: (value) => ({
      min: Math.max(0, value - 1),
      max: value + 1,
    }),
  },
  {
    formKey: "coveredArea",
    wdKey: "square_meters_covered",
    label: "m² Cubiertos",
    placeholder: "Ej: 85.5",
    required: true,
    minValue: 1,
    allowDecimal: true,
    minFilter: "minSquareMetersCovered",
    maxFilter: "maxSquareMetersCovered",
    expandExact: (value) => ({
      min: Math.max(0, value - 50),
      max: value + 50,
    }),
  },
  {
    formKey: "totalArea",
    wdKey: "square_meters_total",
    label: "m² Totales",
    placeholder: "Ej: 120.5",
    required: true,
    minValue: 1,
    allowDecimal: true,
    minFilter: "minSquareMetersTotal",
    maxFilter: "maxSquareMetersTotal",
    expandExact: (value) => ({
      min: Math.max(0, value - 50),
      max: value + 50,
    }),
  },
];

export function parseOptionalInt(value) {
  if (value === "" || value == null) return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

export function parseOptionalNumber(value) {
  if (value === "" || value == null) return null;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function parseCharacteristicValue(value, allowDecimal = false) {
  return allowDecimal ? parseOptionalNumber(value) : parseOptionalInt(value);
}

function getConfigByFormKey(formKey) {
  return WIZARD_CHARACTERISTICS.find((config) => config.formKey === formKey);
}

export function getCharacteristicReference(wd, formKey, wdKey) {
  const allowDecimal = !!getConfigByFormKey(formKey)?.allowDecimal;
  const useRange = !!wd[`${formKey}UseRange`];
  if (useRange) {
    const min = parseCharacteristicValue(wd[`${formKey}Min`], allowDecimal);
    const max = parseCharacteristicValue(wd[`${formKey}Max`], allowDecimal);
    if (min != null && max != null) {
      const avg = (min + max) / 2;
      return allowDecimal ? avg : Math.round(avg);
    }
    if (min != null) return min;
    if (max != null) return max;
    return null;
  }
  const raw = wd[formKey] ?? wd[wdKey];
  return parseCharacteristicValue(raw, allowDecimal);
}

export function formatCharacteristicDisplay(
  wd,
  { formKey, wdKey },
  suffix = "",
) {
  const useRange = !!wd[`${formKey}UseRange`];
  if (useRange) {
    const min = wd[`${formKey}Min`];
    const max = wd[`${formKey}Max`];
    const hasMin = min !== "" && min != null;
    const hasMax = max !== "" && max != null;
    if (hasMin && hasMax) return `${min} – ${max}${suffix}`;
    if (hasMin) return `desde ${min}${suffix}`;
    if (hasMax) return `hasta ${max}${suffix}`;
    return "N/A";
  }
  const exact = wd[formKey] ?? wd[wdKey];
  return exact !== "" && exact != null ? `${exact}${suffix}` : "N/A";
}

export function buildFilterBoundsFromWizard(wd, config) {
  const { formKey, wdKey, minFilter, maxFilter, expandExact, allowDecimal = false } = config;
  const useRange = !!wd[`${formKey}UseRange`];

  if (useRange) {
    return {
      [minFilter]: parseCharacteristicValue(wd[`${formKey}Min`], allowDecimal),
      [maxFilter]: parseCharacteristicValue(wd[`${formKey}Max`], allowDecimal),
    };
  }

  const exact = getCharacteristicReference(wd, formKey, wdKey);
  if (exact == null) {
    return { [minFilter]: null, [maxFilter]: null };
  }

  const expanded = expandExact(exact);
  return {
    [minFilter]: expanded.min,
    [maxFilter]: expanded.max,
  };
}

export function getApplicableCharacteristics(typeProperty) {
  if (typeProperty === "terrenos") {
    return WIZARD_CHARACTERISTICS.filter((config) => config.formKey === "totalArea");
  }
  return WIZARD_CHARACTERISTICS;
}

function belowMinValue(value, minValue) {
  return value == null || value < minValue;
}

export function validateCharacteristicFields(
  formData,
  characteristics = WIZARD_CHARACTERISTICS,
) {
  for (const config of characteristics) {
    const { formKey, label, required, minValue = 0, allowDecimal = false } = config;
    if (!required) continue;

    const useRange = !!formData[`${formKey}UseRange`];
    const labelLower = label.toLowerCase();

    if (!useRange) {
      const exact = parseCharacteristicValue(formData[formKey], allowDecimal);
      if (exact == null) {
        return {
          ok: false,
          message: `Debés ingresar ${labelLower} de la propiedad.`,
        };
      }
      if (belowMinValue(exact, minValue)) {
        return {
          ok: false,
          message:
            minValue > 0
              ? `${label} debe ser ${minValue} o más.`
              : `Debés ingresar ${labelLower} de la propiedad.`,
        };
      }
      continue;
    }

    const min = parseCharacteristicValue(formData[`${formKey}Min`], allowDecimal);
    const max = parseCharacteristicValue(formData[`${formKey}Max`], allowDecimal);

    if (min == null || max == null) {
      return {
        ok: false,
        message: `Debés completar el mínimo y el máximo de ${labelLower}.`,
      };
    }

    if (min > max) {
      return {
        ok: false,
        message: `En ${labelLower}, el mínimo no puede ser mayor que el máximo.`,
      };
    }

    if (belowMinValue(min, minValue) || belowMinValue(max, minValue)) {
      return {
        ok: false,
        message:
          minValue > 0
            ? `${label} debe tener valores de ${minValue} o más en el rango.`
            : `Debés completar el mínimo y el máximo de ${labelLower}.`,
      };
    }
  }

  return { ok: true };
}

export function appendCharacteristicFieldsToPayload(
  target,
  formData,
  characteristics = WIZARD_CHARACTERISTICS,
) {
  const applicable = new Set(characteristics.map((config) => config.formKey));

  WIZARD_CHARACTERISTICS.forEach(({ formKey, wdKey }) => {
    if (!applicable.has(formKey)) {
      target[`${formKey}UseRange`] = false;
      target[`${formKey}Min`] = "";
      target[`${formKey}Max`] = "";
      target[formKey] = "";
      target[wdKey] = null;
      return;
    }

    const useRange = !!formData[`${formKey}UseRange`];
    target[`${formKey}UseRange`] = useRange;
    target[`${formKey}Min`] = formData[`${formKey}Min`] ?? "";
    target[`${formKey}Max`] = formData[`${formKey}Max`] ?? "";
    target[formKey] = useRange ? "" : (formData[formKey] ?? "");
    target[wdKey] = getCharacteristicReference({ ...formData }, formKey, wdKey);
  });
}

export function mapSessionCharacteristicFields(source, target = {}) {
  WIZARD_CHARACTERISTICS.forEach(({ formKey, wdKey }) => {
    target[`${formKey}UseRange`] = !!source[`${formKey}UseRange`];
    target[`${formKey}Min`] = source[`${formKey}Min`] ?? "";
    target[`${formKey}Max`] = source[`${formKey}Max`] ?? "";

    if (!target[`${formKey}UseRange`]) {
      const exact = source[formKey] ?? source[wdKey];
      target[formKey] = exact != null && exact !== "" ? String(exact) : "";
    } else {
      target[formKey] = source[formKey] ?? "";
    }

    const ref = getCharacteristicReference(
      { ...source, ...target },
      formKey,
      wdKey,
    );
    if (ref != null) target[wdKey] = ref;
  });
  return target;
}
